import React, { useState } from 'react';
import { APP } from './tokens';
import type { RightPanelSettings as ToolPanelSettings } from '../../types';

// ─── Types ─────────────────────────────────────────────

export interface ToolDef {
  id: string;
  label: string;
  icon: (stroke: string) => React.ReactNode;
  hasSettings?: boolean;
  renderSettings?: (ctx: SettingsCtx) => React.ReactNode;
}

export interface ToolCategory {
  id: string;
  label: string;
  tools: ToolDef[];
}

interface SettingsCtx {
  settings: ToolPanelSettings;
  onSettingsChange: (patch: Partial<ToolPanelSettings>) => void;
}

// ─── Settings Primitives ───────────────────────────────

const FONT = APP.fontFamily;

function Toggle({ label, sublabel, value, onChange }: {
  label: string; sublabel?: string; value: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <div
      onClick={() => onChange(!value)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onChange(!value)}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4, cursor: 'pointer', fontFamily: FONT }}
    >
      <div>
        <span style={{ fontSize: 11, color: APP.textPrimary }}>{label}</span>
        {sublabel && <div style={{ fontSize: 10, color: APP.textMuted, marginTop: 1 }}>{sublabel}</div>}
      </div>
      <div style={{
        width: 28, height: 16, borderRadius: 9999, flexShrink: 0,
        background: value ? APP.strokeToolActive : APP.border,
        position: 'relative', transition: 'background 150ms ease',
      }}>
        <div style={{
          position: 'absolute', top: 1, left: value ? 13 : 1,
          width: 14, height: 14, borderRadius: 9999, background: '#FFFFFF',
          transition: 'left 150ms ease',
        }} />
      </div>
    </div>
  );
}

function Slider({ label, value, min, max, step = 1, onChange, formatValue = (v: number) => String(v) }: {
  label: string; value: number; min: number; max: number; step?: number;
  onChange: (v: number) => void; formatValue?: (v: number) => string;
}) {
  return (
    <div style={{ marginBottom: 4, fontFamily: FONT }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
        <span style={{ fontSize: 11, color: APP.textMuted }}>{label}</span>
        <span style={{ fontSize: 11, color: APP.strokeToolActive }}>{formatValue(value)}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ accentColor: APP.strokeToolActive, width: '100%', cursor: 'pointer', height: 4 }}
      />
    </div>
  );
}

function Select({ label, value, options, onChange }: {
  label: string; value: string; options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ marginBottom: 4, fontFamily: FONT }}>
      <span style={{ display: 'block', fontSize: 11, color: APP.textMuted, marginBottom: 2 }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', padding: '4px 6px', fontSize: 11, color: APP.textPrimary,
          background: '#FFFFFF', border: `1px solid ${APP.border}`, borderRadius: 6,
          fontFamily: FONT, cursor: 'pointer',
        }}
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function TextInput({ label, value, placeholder, onChange }: {
  label: string; value: string; placeholder?: string; onChange: (v: string) => void;
}) {
  return (
    <div style={{ marginBottom: 4, fontFamily: FONT }}>
      <span style={{ display: 'block', fontSize: 11, color: APP.textMuted, marginBottom: 2 }}>{label}</span>
      <input
        type="text" value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', padding: '4px 6px', fontSize: 11, color: APP.textPrimary,
          background: '#FFFFFF', border: `1px solid ${APP.border}`, borderRadius: 6,
          fontFamily: FONT, boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

function NumberInput({ label, value, placeholder, min, max, step, onChange }: {
  label: string; value?: number; placeholder?: string; min?: number; max?: number; step?: number;
  onChange: (v: number | undefined) => void;
}) {
  return (
    <div style={{ marginBottom: 4, fontFamily: FONT }}>
      <span style={{ display: 'block', fontSize: 11, color: APP.textMuted, marginBottom: 2 }}>{label}</span>
      <input
        type="number" value={value ?? ''} placeholder={placeholder} min={min} max={max} step={step}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
        style={{
          width: '100%', padding: '4px 6px', fontSize: 11, color: APP.textPrimary,
          background: '#FFFFFF', border: `1px solid ${APP.border}`, borderRadius: 6,
          fontFamily: FONT, boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

function TextArea({ label, value, placeholder, rows = 3, onChange }: {
  label: string; value: string; placeholder?: string; rows?: number; onChange: (v: string) => void;
}) {
  return (
    <div style={{ marginBottom: 4, fontFamily: FONT }}>
      <span style={{ display: 'block', fontSize: 11, color: APP.textMuted, marginBottom: 2 }}>{label}</span>
      <textarea
        value={value} placeholder={placeholder} rows={rows}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', padding: '4px 6px', fontSize: 11, color: APP.textPrimary,
          background: '#FFFFFF', border: `1px solid ${APP.border}`, borderRadius: 6,
          fontFamily: FONT, resize: 'vertical', boxSizing: 'border-box',
        }}
      />
    </div>
  );
}

function SegmentedControl<T extends string>({ label, value, options, onChange }: {
  label: string; value: T; options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div style={{ marginBottom: 4, fontFamily: FONT }}>
      <span style={{ display: 'block', fontSize: 11, color: APP.textMuted, marginBottom: 3 }}>{label}</span>
      <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: `1px solid ${APP.border}` }}>
        {options.map((o) => (
          <button
            key={o.value} type="button" onClick={() => onChange(o.value)}
            style={{
              flex: 1, padding: '4px 0', fontSize: 10, fontFamily: FONT, fontWeight: 500,
              border: 'none', cursor: 'pointer',
              background: value === o.value ? APP.strokeToolActive : '#FFFFFF',
              color: value === o.value ? '#FFFFFF' : APP.textPrimary,
            }}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ColorPicker({ label, value, onChange }: {
  label: string; value: string; onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontFamily: FONT }}>
      <span style={{ fontSize: 11, color: APP.textMuted, flex: 1 }}>{label}</span>
      <input
        type="color" value={value} onChange={(e) => onChange(e.target.value)}
        style={{ width: 24, height: 20, border: `1px solid ${APP.border}`, borderRadius: 4, cursor: 'pointer', padding: 0 }}
      />
      <span style={{ fontSize: 10, color: APP.textMuted, width: 52 }}>{value}</span>
    </div>
  );
}

function MultiSelect({ label, options, value, onChange }: {
  label: string; options: { value: string; label: string }[]; value: string[];
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 4, fontFamily: FONT }}>
      <span style={{ display: 'block', fontSize: 11, color: APP.textMuted, marginBottom: 2 }}>{label}</span>
      <button
        type="button" onClick={() => setOpen(!open)}
        style={{
          width: '100%', padding: '4px 6px', fontSize: 11, color: APP.textPrimary,
          background: '#FFFFFF', border: `1px solid ${APP.border}`, borderRadius: 6,
          fontFamily: FONT, cursor: 'pointer', textAlign: 'left',
        }}
      >
        {value.length ? options.filter(o => value.includes(o.value)).map(o => o.label).join(', ') : 'Select...'}
      </button>
      {open && (
        <div style={{ border: `1px solid ${APP.border}`, borderRadius: 6, marginTop: 2, background: '#FFFFFF', maxHeight: 140, overflow: 'auto' }}>
          {options.map((o) => (
            <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 6px', fontSize: 11, cursor: 'pointer' }}>
              <input
                type="checkbox" checked={value.includes(o.value)}
                onChange={(e) => {
                  onChange(e.target.checked ? [...value, o.value] : value.filter(v => v !== o.value));
                }}
              />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function PositionGrid({ label, value, onChange }: {
  label: string; value: string; onChange: (v: 'TL' | 'TC' | 'TR' | 'ML' | 'MC' | 'MR' | 'BL' | 'BC' | 'BR') => void;
}) {
  const positions: ('TL' | 'TC' | 'TR' | 'ML' | 'MC' | 'MR' | 'BL' | 'BC' | 'BR')[] =
    ['TL', 'TC', 'TR', 'ML', 'MC', 'MR', 'BL', 'BC', 'BR'];
  return (
    <div style={{ marginBottom: 4, fontFamily: FONT }}>
      <span style={{ display: 'block', fontSize: 11, color: APP.textMuted, marginBottom: 3 }}>{label}</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', width: 60, gap: 2 }}>
        {positions.map((p) => (
          <button
            key={p} type="button" onClick={() => onChange(p)}
            style={{
              width: 18, height: 18, borderRadius: 3, border: `1px solid ${APP.border}`,
              background: value === p ? APP.strokeToolActive : '#FFFFFF', cursor: 'pointer', padding: 0,
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── OCR Languages ────────────────────────────────────

const OCR_LANGS = [
  { value: 'eng', label: 'English' }, { value: 'spa', label: 'Spanish' },
  { value: 'fra', label: 'French' }, { value: 'deu', label: 'German' },
  { value: 'por', label: 'Portuguese' }, { value: 'chi_sim', label: 'Chinese' },
  { value: 'ara', label: 'Arabic' }, { value: 'hin', label: 'Hindi' },
  { value: 'jpn', label: 'Japanese' }, { value: 'kor', label: 'Korean' },
];

// ─── SVG Icon Helpers ─────────────────────────────────

const svgProps = (size = 13) => ({
  width: size, height: size, viewBox: '0 0 24 24', fill: 'none',
  strokeWidth: 1.5, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  style: { flexShrink: 0 } as React.CSSProperties,
});

// ─── Tool Categories ──────────────────────────────────

export function buildToolCategories(): ToolCategory[] {
  return [
    // ──────── ANALYZE & UNDERSTAND ────────
    {
      id: 'analyze',
      label: 'ANALYZE & UNDERSTAND',
      tools: [
        {
          id: 'chat_with_document', label: 'Chat with document',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <Select label="Context" value={s.chatContext ?? 'full'} onChange={(v) => ch({ chatContext: v })} options={[{ value: 'full', label: 'Full document' }, { value: 'current_page', label: 'Current page' }, { value: 'selection', label: 'Selection' }]} />
          ),
        },
        {
          id: 'summarize', label: 'Summarize',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><line x1="8" y1="13" x2="16" y2="13" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <SegmentedControl label="Length" value={s.summaryLength ?? 'medium'} onChange={(v) => ch({ summaryLength: v })} options={[{ value: 'short', label: 'Short' }, { value: 'medium', label: 'Medium' }, { value: 'long', label: 'Long' }]} />
              <Select label="Format" value={s.summaryFormat ?? 'paragraph'} onChange={(v) => ch({ summaryFormat: v as 'paragraph' | 'bullets' | 'outline' })} options={[{ value: 'paragraph', label: 'Paragraph' }, { value: 'bullets', label: 'Bullet points' }, { value: 'outline', label: 'Outline' }]} />
            </>
          ),
        },
        {
          id: 'extract_text_analyze', label: 'Extract text',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M7 7h10" /><path d="M7 12h4" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <SegmentedControl label="Output" value={s.extractTextFormat ?? 'plain'} onChange={(v) => ch({ extractTextFormat: v })} options={[{ value: 'plain', label: 'Plain' }, { value: 'markdown', label: 'Markdown' }, { value: 'structured', label: 'Structured' }]} />
          ),
        },
        {
          id: 'key_terms', label: 'Key terms & citations',
          icon: (s) => <svg {...svgProps()} stroke={s}><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Toggle label="Include citations" value={s.keyTermsIncludeCitations ?? true} onChange={(v) => ch({ keyTermsIncludeCitations: v })} />
              <NumberInput label="Max terms" value={s.keyTermsMaxCount ?? 20} onChange={(v) => ch({ keyTermsMaxCount: v })} />
            </>
          ),
        },
      ],
    },
    // ──────── CONVERT & TRANSFORM ────────
    {
      id: 'convert',
      label: 'CONVERT & TRANSFORM',
      tools: [
        {
          id: 'pdf_to_word', label: 'PDF → Word',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 15l2 2 4-4" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Select label="Output format" value={s.pdfToWordOutputFormat ?? 'docx'} onChange={(v) => ch({ pdfToWordOutputFormat: v as 'docx' | 'doc' })} options={[{ value: 'docx', label: 'DOCX' }, { value: 'doc', label: 'DOC' }]} />
              <TextInput label="Page range" value={s.pageRange ?? ''} placeholder="All pages" onChange={(v) => ch({ pageRange: v })} />
              <Toggle label="Preserve formatting" value={s.preserveFormatting ?? false} onChange={(v) => ch({ preserveFormatting: v })} />
              <Slider label="Image quality" value={s.pdfToWordImageQuality ?? 75} min={50} max={100} onChange={(v) => ch({ pdfToWordImageQuality: v })} formatValue={(v) => v + '%'} />
            </>
          ),
        },
        {
          id: 'pdf_to_powerpoint', label: 'PDF → PowerPoint',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8" /><path d="M12 17v4" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <TextInput label="Page range" value={s.pageRange ?? ''} placeholder="e.g. 1–3, 5" onChange={(v) => ch({ pageRange: v })} />
              <SegmentedControl label="Slide size" value={s.pdfToPptSlideSize ?? '16:9'} onChange={(v) => ch({ pdfToPptSlideSize: v })} options={[{ value: '16:9', label: '16:9' }, { value: '4:3', label: '4:3' }, { value: 'standard', label: 'Standard' }]} />
              <Slider label="Image quality" value={s.pdfToPptImageQuality ?? 85} min={50} max={100} onChange={(v) => ch({ pdfToPptImageQuality: v })} formatValue={(v) => v + '%'} />
            </>
          ),
        },
        {
          id: 'pdf_to_excel', label: 'PDF → Excel',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /><path d="M3 15h18" /><path d="M9 3v18" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <TextInput label="Page range" value={s.pageRange ?? ''} placeholder="e.g. 1–3" onChange={(v) => ch({ pageRange: v })} />
              <SegmentedControl label="Output format" value={s.pdfToExcelOutputFormat ?? 'xlsx'} onChange={(v) => ch({ pdfToExcelOutputFormat: v })} options={[{ value: 'xlsx', label: 'XLSX' }, { value: 'xls', label: 'XLS' }]} />
              <Toggle label="Extract tables only" value={s.extractTablesOnly ?? false} onChange={(v) => ch({ extractTablesOnly: v })} />
            </>
          ),
        },
        {
          id: 'compress_pdf', label: 'Compress PDF',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M12 3v14" /><rect x="4" y="21" width="16" height="2" rx="1" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <SegmentedControl label="Quality" value={s.pdfQuality ?? 'ebook'} onChange={(v) => ch({ pdfQuality: v })} options={[{ value: 'screen', label: 'Low' }, { value: 'ebook', label: 'Med' }, { value: 'printer', label: 'High' }, { value: 'prepress', label: 'Max' }]} />
              <Slider label="JPEG quality" value={s.compressJpegQuality ?? 60} min={1} max={100} onChange={(v) => ch({ compressJpegQuality: v })} formatValue={(v) => v + '%'} />
              <Toggle label="Optimize for web" value={s.optimizeForWeb ?? false} onChange={(v) => ch({ optimizeForWeb: v })} />
            </>
          ),
        },
        {
          id: 'merge_pdf', label: 'Merge PDFs',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 15l2 2 4-4" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <SegmentedControl label="File order" value={s.mergeOrder ?? 'manual'} onChange={(v) => ch({ mergeOrder: v })} options={[{ value: 'manual', label: 'Manual' }, { value: 'alphabetical', label: 'A–Z' }, { value: 'date', label: 'Date' }]} />
              <Toggle label="Preserve bookmarks" value={s.mergePreserveBookmarks ?? true} onChange={(v) => ch({ mergePreserveBookmarks: v })} />
              <Toggle label="Add table of contents" value={s.mergeAddTableOfContents ?? false} onChange={(v) => ch({ mergeAddTableOfContents: v })} />
            </>
          ),
        },
        {
          id: 'split_pdf', label: 'Split PDF',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><line x1="4" y1="12" x2="20" y2="12" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <SegmentedControl label="Split by" value={s.splitMode ?? 'every'} onChange={(v) => ch({ splitMode: v })} options={[{ value: 'every', label: 'Every page' }, { value: 'range', label: 'By range' }, { value: 'size', label: 'By size' }]} />
              {s.splitMode === 'every' && <NumberInput label="Every N pages" value={s.splitEveryN ?? 1} onChange={(v) => ch({ splitEveryN: v })} />}
              {s.splitMode === 'range' && <TextInput label="Page range" value={s.pageRange ?? ''} placeholder="e.g. 1–3, 5" onChange={(v) => ch({ pageRange: v })} />}
              {s.splitMode === 'size' && <NumberInput label="Max size (MB)" value={s.splitMaxSizeMb} onChange={(v) => ch({ splitMaxSizeMb: v })} />}
            </>
          ),
        },
        {
          id: 'rotate_pdf', label: 'Rotate PDF',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Select label="Angle" value={String(s.rotateAngle ?? 90)} onChange={(v) => ch({ rotateAngle: Number(v) as 90 | 180 | 270 })} options={[{ value: '90', label: '90°' }, { value: '180', label: '180°' }, { value: '270', label: '270°' }]} />
              <SegmentedControl label="Direction" value={s.rotateDirection ?? 'clockwise'} onChange={(v) => ch({ rotateDirection: v })} options={[{ value: 'clockwise', label: 'Clockwise' }, { value: 'counter', label: 'Counter' }]} />
            </>
          ),
        },
        {
          id: 'crop_pdf', label: 'Crop PDF',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M6.13 1L6 16a2 2 0 0 0 2 2h15" /><path d="M1 6.13L16 6a2 2 0 0 1 2 2v15" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Select label="Units" value={s.cropPdfUnits ?? 'pt'} onChange={(v) => ch({ cropPdfUnits: v as ToolPanelSettings['cropPdfUnits'] })} options={[{ value: 'pt', label: 'Points' }, { value: 'mm', label: 'mm' }, { value: 'in', label: 'in' }]} />
              <NumberInput label="Top" value={s.cropMarginTop} onChange={(v) => ch({ cropMarginTop: v })} />
              <NumberInput label="Bottom" value={s.cropMarginBottom} onChange={(v) => ch({ cropMarginBottom: v })} />
              <Toggle label="Crop marks" value={s.cropMarks ?? false} onChange={(v) => ch({ cropMarks: v })} />
            </>
          ),
        },
        {
          id: 'ocr', label: 'OCR PDF',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="7" height="7" /><path d="M14 7h7" /><path d="M14 14h7" /><path d="M3 14h3v3" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <MultiSelect label="Language" options={OCR_LANGS} value={s.ocrLanguages ?? ['eng']} onChange={(v) => ch({ ocrLanguages: v })} />
              <SegmentedControl label="Output format" value={s.ocrOutputFormat ?? 'text'} onChange={(v) => ch({ ocrOutputFormat: v })} options={[{ value: 'text', label: 'Text' }, { value: 'markdown', label: 'Markdown' }, { value: 'json', label: 'JSON' }]} />
              <Toggle label="Preserve layout" value={s.preserveLayout ?? false} onChange={(v) => ch({ preserveLayout: v })} />
            </>
          ),
        },
        {
          id: 'add_page_numbers', label: 'Add Page Numbers',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M12 8v8" /><path d="M8.5 14h1" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Select label="Position" value={s.pageNumberPosition ?? 'bottom-center'} onChange={(v) => ch({ pageNumberPosition: v })} options={[{ value: 'top-left', label: 'Top left' }, { value: 'top-center', label: 'Top center' }, { value: 'top-right', label: 'Top right' }, { value: 'bottom-left', label: 'Bottom left' }, { value: 'bottom-center', label: 'Bottom center' }, { value: 'bottom-right', label: 'Bottom right' }]} />
              <NumberInput label="Start number" value={s.pageNumberStart} onChange={(v) => ch({ pageNumberStart: v })} />
              <Slider label="Font size" value={s.pageNumberFontSize ?? 12} min={8} max={24} onChange={(v) => ch({ pageNumberFontSize: v })} formatValue={(v) => v + 'px'} />
            </>
          ),
        },
        {
          id: 'repair_pdf', label: 'Repair PDF',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>,
        },
        {
          id: 'redact_pdf', label: 'Redact PDF',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="18" height="18" rx="2" /><rect x="7" y="7" width="4" height="1" rx="0.5" /><rect x="7" y="11" width="6" height="1" rx="0.5" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <TextArea label="Search terms" value={s.redactSearchTerms ?? ''} placeholder="One term per line" onChange={(v) => ch({ redactSearchTerms: v })} rows={3} />
              <SegmentedControl label="Redact style" value={s.redactStyle ?? 'blackout'} onChange={(v) => ch({ redactStyle: v as ToolPanelSettings['redactStyle'] })} options={[{ value: 'blackout', label: 'Blackout' }, { value: 'whiteout', label: 'Whiteout' }]} />
              <Toggle label="Case sensitive" value={s.redactCaseSensitive ?? false} onChange={(v) => ch({ redactCaseSensitive: v })} />
            </>
          ),
        },
      ],
    },
    // ──────── EDIT & ORGANIZE ────────
    {
      id: 'edit',
      label: 'EDIT & ORGANIZE',
      tools: [
        {
          id: 'page_setup', label: 'Page Setup',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Select label="Page size" value={s.pageSize ?? 'a4'} onChange={(v) => ch({ pageSize: v as ToolPanelSettings['pageSize'] })} options={[{ value: 'a4', label: 'A4' }, { value: 'letter', label: 'Letter' }, { value: 'legal', label: 'Legal' }, { value: 'tabloid', label: 'Tabloid' }]} />
              <SegmentedControl label="Orientation" value={s.pageOrientation ?? 'portrait'} onChange={(v) => ch({ pageOrientation: v })} options={[{ value: 'portrait', label: 'Portrait' }, { value: 'landscape', label: 'Landscape' }]} />
            </>
          ),
        },
        {
          id: 'document_properties', label: 'Document Properties',
          icon: (s) => <svg {...svgProps()} stroke={s}><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="8" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <TextInput label="Title" value={s.documentTitle ?? ''} placeholder="Document title" onChange={(v) => ch({ documentTitle: v })} />
              <TextInput label="Author" value={s.documentAuthor ?? ''} placeholder="Author name" onChange={(v) => ch({ documentAuthor: v })} />
              <TextInput label="Subject" value={s.documentSubject ?? ''} placeholder="Subject" onChange={(v) => ch({ documentSubject: v })} />
              <TextInput label="Keywords" value={s.documentKeywords ?? ''} placeholder="Comma-separated" onChange={(v) => ch({ documentKeywords: v })} />
            </>
          ),
        },
        {
          id: 'headers_footers', label: 'Headers & Footers',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /><path d="M3 15h18" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <TextInput label="Header text" value={s.headerText ?? ''} placeholder="e.g. Company name" onChange={(v) => ch({ headerText: v })} />
              <TextInput label="Footer text" value={s.footerText ?? ''} placeholder="e.g. Confidential" onChange={(v) => ch({ footerText: v })} />
              <Toggle label="Different first page" value={s.headerFooterDifferentFirstPage ?? false} onChange={(v) => ch({ headerFooterDifferentFirstPage: v })} />
              <Toggle label="Page number in footer" value={s.pageNumberInFooter ?? true} onChange={(v) => ch({ pageNumberInFooter: v })} />
            </>
          ),
        },
        {
          id: 'typography_defaults', label: 'Typography Defaults',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M4 7V4h16v3" /><line x1="9" y1="20" x2="15" y2="20" /><line x1="12" y1="4" x2="12" y2="20" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Select label="Default font" value={s.defaultFontFamily ?? 'Helvetica'} onChange={(v) => ch({ defaultFontFamily: v })} options={[{ value: 'Helvetica', label: 'Helvetica' }, { value: 'Arial', label: 'Arial' }, { value: 'Times New Roman', label: 'Times New Roman' }, { value: 'Georgia', label: 'Georgia' }]} />
              <NumberInput label="Font size (pt)" value={s.defaultFontSize} placeholder="12" onChange={(v) => ch({ defaultFontSize: v })} />
              <SegmentedControl label="Line spacing" value={s.lineSpacing ?? 'single'} onChange={(v) => ch({ lineSpacing: v })} options={[{ value: 'single', label: 'Single' }, { value: '1.5', label: '1.5' }, { value: 'double', label: 'Double' }]} />
            </>
          ),
        },
        {
          id: 'export_options', label: 'Export & Print',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Toggle label="Fit to page" value={s.fitToPage ?? false} onChange={(v) => ch({ fitToPage: v })} />
              <Toggle label="Embed fonts" value={s.embedFonts ?? false} onChange={(v) => ch({ embedFonts: v })} />
              <Toggle label="Tagged PDF" sublabel="Accessibility" value={s.taggedPdf ?? false} onChange={(v) => ch({ taggedPdf: v })} />
            </>
          ),
        },
      ],
    },
    // ──────── IMAGES ────────
    {
      id: 'images',
      label: 'IMAGES',
      tools: [
        {
          id: 'compress_image', label: 'Compress Image',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Slider label="Quality" value={s.imageQuality ?? 75} min={1} max={100} onChange={(v) => ch({ imageQuality: v })} formatValue={(v) => v + '%'} />
              <Toggle label="Strip metadata" value={s.stripMetadata ?? false} onChange={(v) => ch({ stripMetadata: v })} />
            </>
          ),
        },
        {
          id: 'convert_image', label: 'Convert Format',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <SegmentedControl label="Format" value={s.outputFormat ?? 'png'} onChange={(v) => ch({ outputFormat: v })} options={[{ value: 'jpg', label: 'JPG' }, { value: 'png', label: 'PNG' }, { value: 'webp', label: 'WEBP' }, { value: 'avif', label: 'AVIF' }]} />
              <Slider label="Quality" value={s.imageQuality ?? 85} min={1} max={100} onChange={(v) => ch({ imageQuality: v })} formatValue={(v) => v + '%'} />
              <Toggle label="Preserve transparency" value={s.preserveTransparency ?? false} onChange={(v) => ch({ preserveTransparency: v })} />
            </>
          ),
        },
        {
          id: 'resize_image', label: 'Resize Image',
          icon: (s) => <svg {...svgProps()} stroke={s}><polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <NumberInput label="Width (px)" value={s.resizeWidth} placeholder="Auto" onChange={(v) => ch({ resizeWidth: v })} />
              <NumberInput label="Height (px)" value={s.resizeHeight} placeholder="Auto" onChange={(v) => ch({ resizeHeight: v })} />
              <Toggle label="Maintain aspect ratio" value={s.maintainAspectRatio ?? true} onChange={(v) => ch({ maintainAspectRatio: v })} />
            </>
          ),
        },
        {
          id: 'remove_background', label: 'Remove Background',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18" /><circle cx="6" cy="12" r="1" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Slider label="Tolerance" value={s.bgRemovalTolerance ?? 50} min={0} max={100} onChange={(v) => ch({ bgRemovalTolerance: v })} formatValue={(v) => v + '%'} />
              <Toggle label="Keep shadow" value={s.bgRemovalKeepShadow ?? false} onChange={(v) => ch({ bgRemovalKeepShadow: v })} />
            </>
          ),
        },
        {
          id: 'crop_image', label: 'Crop Image',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M6.13 1L6 16a2 2 0 0 0 2 2h15" /><path d="M1 6.13L16 6a2 2 0 0 1 2 2v15" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Select label="Aspect ratio" value={s.cropAspectRatio ?? 'free'} onChange={(v) => ch({ cropAspectRatio: v })} options={[{ value: 'free', label: 'Free' }, { value: '16:9', label: '16:9' }, { value: '4:3', label: '4:3' }, { value: '1:1', label: '1:1' }]} />
              <NumberInput label="Width (px)" value={s.cropWidth} onChange={(v) => ch({ cropWidth: v })} />
              <NumberInput label="Height (px)" value={s.cropHeight} onChange={(v) => ch({ cropHeight: v })} />
            </>
          ),
        },
        {
          id: 'flip_rotate_image', label: 'Flip / Rotate',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <SegmentedControl label="Action" value={s.flipRotateAction ?? 'flip_h'} onChange={(v) => ch({ flipRotateAction: v })} options={[{ value: 'flip_h', label: 'Flip H' }, { value: 'flip_v', label: 'Flip V' }, { value: 'rotate_90', label: '90°' }, { value: 'rotate_180', label: '180°' }]} />
            </>
          ),
        },
        {
          id: 'add_border', label: 'Add Border',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="18" height="18" rx="2" /><rect x="6" y="6" width="12" height="12" rx="1" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Slider label="Width" value={s.borderWidth ?? 1} min={1} max={50} onChange={(v) => ch({ borderWidth: v })} formatValue={(v) => v + 'px'} />
              <ColorPicker label="Color" value={s.borderColor ?? '#000000'} onChange={(v) => ch({ borderColor: v })} />
            </>
          ),
        },
        {
          id: 'upscale_image', label: 'Upscale Image',
          icon: (s) => <svg {...svgProps()} stroke={s}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <SegmentedControl label="Scale" value={s.upscaleScale ?? '2x'} onChange={(v) => ch({ upscaleScale: v })} options={[{ value: '2x', label: '2x' }, { value: '4x', label: '4x' }]} />
              <Toggle label="Denoise" value={s.upscaleDenoise ?? false} onChange={(v) => ch({ upscaleDenoise: v })} />
              <Toggle label="Face enhance" value={s.upscaleFaceEnhance ?? false} onChange={(v) => ch({ upscaleFaceEnhance: v })} />
            </>
          ),
        },
        {
          id: 'image_to_pdf', label: 'Convert to PDF',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Select label="Page size" value={s.imageToPdfPageSize ?? 'a4'} onChange={(v) => ch({ imageToPdfPageSize: v as ToolPanelSettings['imageToPdfPageSize'] })} options={[{ value: 'a4', label: 'A4' }, { value: 'letter', label: 'Letter' }, { value: 'legal', label: 'Legal' }]} />
              <SegmentedControl label="Orientation" value={s.imageToPdfOrientation ?? 'portrait'} onChange={(v) => ch({ imageToPdfOrientation: v })} options={[{ value: 'portrait', label: 'Portrait' }, { value: 'landscape', label: 'Landscape' }]} />
            </>
          ),
        },
        {
          id: 'watermark_image', label: 'Watermark Image',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 15l5-5 3 3 4-4 6 6" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <TextInput label="Text" value={s.watermarkText ?? ''} onChange={(v) => ch({ watermarkText: v })} />
              <Slider label="Opacity" value={s.watermarkOpacity ?? 50} min={5} max={100} onChange={(v) => ch({ watermarkOpacity: v })} formatValue={(v) => v + '%'} />
              <PositionGrid label="Position" value={s.watermarkPosition ?? 'MC'} onChange={(v) => ch({ watermarkPosition: v })} />
            </>
          ),
        },
        {
          id: 'extract_text_image', label: 'Extract Text (OCR)',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="7" height="7" /><path d="M14 7h7" /><path d="M14 14h7" /><path d="M3 14h3v3" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <MultiSelect label="Language" options={OCR_LANGS} value={s.ocrLanguages ?? ['eng']} onChange={(v) => ch({ ocrLanguages: v })} />
              <SegmentedControl label="Output" value={s.ocrOutputFormat ?? 'text'} onChange={(v) => ch({ ocrOutputFormat: v })} options={[{ value: 'text', label: 'Text' }, { value: 'markdown', label: 'Markdown' }]} />
            </>
          ),
        },
      ],
    },
    // ──────── AUDIO ────────
    {
      id: 'audio',
      label: 'AUDIO',
      tools: [
        {
          id: 'transcribe_audio', label: 'Transcribe Audio',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Select label="Language" value={s.audioLanguage ?? 'auto'} onChange={(v) => ch({ audioLanguage: v })} options={[{ value: 'auto', label: 'Auto-detect' }, { value: 'en', label: 'English' }, { value: 'es', label: 'Spanish' }, { value: 'fr', label: 'French' }, { value: 'de', label: 'German' }]} />
              <SegmentedControl label="Output" value={s.audioOutputFormat ?? 'timestamped'} onChange={(v) => ch({ audioOutputFormat: v })} options={[{ value: 'plain', label: 'Plain' }, { value: 'timestamped', label: 'Timed' }, { value: 'srt', label: 'SRT' }]} />
              <Toggle label="Speaker diarization" value={s.speakerDiarization ?? false} onChange={(v) => ch({ speakerDiarization: v })} />
            </>
          ),
        },
        {
          id: 'extract_audio', label: 'Extract Audio',
          icon: (s) => <svg {...svgProps()} stroke={s}><circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Select label="Format" value={s.audioExtractFormat ?? 'mp3'} onChange={(v) => ch({ audioExtractFormat: v as ToolPanelSettings['audioExtractFormat'] })} options={[{ value: 'mp3', label: 'MP3' }, { value: 'wav', label: 'WAV' }, { value: 'aac', label: 'AAC' }, { value: 'flac', label: 'FLAC' }]} />
            </>
          ),
        },
        {
          id: 'remove_silence', label: 'Remove Silence',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M11 5L6 9H2v6h4l5 4V5z" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Slider label="Threshold (dB)" value={s.silenceThreshold ?? -40} min={-60} max={-10} onChange={(v) => ch({ silenceThreshold: v })} formatValue={(v) => v + ' dB'} />
              <Slider label="Min silence (ms)" value={s.minSilenceLength ?? 500} min={100} max={5000} step={100} onChange={(v) => ch({ minSilenceLength: v })} formatValue={(v) => v + 'ms'} />
            </>
          ),
        },
        {
          id: 'convert_audio', label: 'Convert Audio Format',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M17 1l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><path d="M7 23l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Select label="Format" value={s.audioConvertFormat ?? 'mp3'} onChange={(v) => ch({ audioConvertFormat: v as ToolPanelSettings['audioConvertFormat'] })} options={[{ value: 'mp3', label: 'MP3' }, { value: 'wav', label: 'WAV' }, { value: 'aac', label: 'AAC' }, { value: 'ogg', label: 'OGG' }, { value: 'flac', label: 'FLAC' }]} />
              <Select label="Bitrate" value={s.audioBitrate ?? '128k'} onChange={(v) => ch({ audioBitrate: v as ToolPanelSettings['audioBitrate'] })} options={[{ value: '64k', label: '64 kbps' }, { value: '128k', label: '128 kbps' }, { value: '192k', label: '192 kbps' }, { value: '320k', label: '320 kbps' }]} />
            </>
          ),
        },
        {
          id: 'audio_to_pdf', label: 'Audio to PDF',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><circle cx="10" cy="16" r="2" /><path d="M12 14V9" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Toggle label="Include timestamps" value={s.includeTimestamps ?? true} onChange={(v) => ch({ includeTimestamps: v })} />
              <Toggle label="Include metadata" value={s.includeMetadataHeader ?? true} onChange={(v) => ch({ includeMetadataHeader: v })} />
            </>
          ),
        },
      ],
    },
    // ──────── SIGN & SEND ────────
    {
      id: 'sign',
      label: 'SIGN & SEND',
      tools: [
        {
          id: 'esign', label: 'E-Sign Document',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <SegmentedControl label="Placement" value={s.placementMode ?? 'auto'} onChange={(v) => ch({ placementMode: v })} options={[{ value: 'auto', label: 'Auto' }, { value: 'manual', label: 'Manual' }]} />
              <Toggle label="Add date stamp" value={s.addDateStamp ?? false} onChange={(v) => ch({ addDateStamp: v })} />
            </>
          ),
        },
        {
          id: 'request_signatures', label: 'Request Signatures',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><circle cx="10" cy="14" r="2" /><path d="M10 16v2" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <SegmentedControl label="Order" value={s.signingOrder ?? 'parallel'} onChange={(v) => ch({ signingOrder: v })} options={[{ value: 'sequential', label: 'Sequential' }, { value: 'parallel', label: 'Parallel' }]} />
              <Toggle label="Auto remind" value={s.autoRemind ?? false} onChange={(v) => ch({ autoRemind: v })} />
            </>
          ),
        },
        {
          id: 'place_fields', label: 'Place Fields',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <Toggle label="Auto-detect fields" value={s.autoDetectFields ?? true} onChange={(v) => ch({ autoDetectFields: v })} />
          ),
        },
        {
          id: 'bulk_send', label: 'Bulk Send',
          icon: (s) => <svg {...svgProps()} stroke={s}><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <Toggle label="Show preview" value={s.bulkSendShowPreview ?? true} onChange={(v) => ch({ bulkSendShowPreview: v })} />
          ),
        },
        {
          id: 'in_person_signing', label: 'In-Person Signing',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <TextInput label="Signer name" value={s.inPersonSignerName ?? ''} onChange={(v) => ch({ inPersonSignerName: v })} />
              <Toggle label="Lock after signing" value={s.lockScreenAfterSigning ?? true} onChange={(v) => ch({ lockScreenAfterSigning: v })} />
            </>
          ),
        },
        {
          id: 'templates', label: 'Templates',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /><path d="M9 21V9" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <TextInput label="Template name" value={s.templateName ?? ''} onChange={(v) => ch({ templateName: v })} />
              <TextInput label="Description" value={s.templateDescription ?? ''} onChange={(v) => ch({ templateDescription: v })} />
            </>
          ),
        },
        {
          id: 'identity_verification', label: 'Identity Verification',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <Select label="Level" value={s.verificationLevel ?? 'standard'} onChange={(v) => ch({ verificationLevel: v as ToolPanelSettings['verificationLevel'] })} options={[{ value: 'standard', label: 'Standard' }, { value: 'enhanced', label: 'Enhanced' }, { value: 'maximum', label: 'Maximum' }]} />
          ),
        },
        {
          id: 'audit_trail', label: 'Audit Trail',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M8 13h2" /><path d="M8 17h2" /></svg>,
        },
        {
          id: 'certificate_completion', label: 'Certificate of Completion',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 15l2 2 4-4" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <Select label="Style" value={s.certificateStyle ?? 'minimal'} onChange={(v) => ch({ certificateStyle: v as ToolPanelSettings['certificateStyle'] })} options={[{ value: 'minimal', label: 'Minimal' }, { value: 'formal', label: 'Formal' }, { value: 'detailed', label: 'Detailed' }]} />
          ),
        },
        {
          id: 'tamper_detection', label: 'Tamper Detection',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="M9 12l2 2 4-4" /></svg>,
        },
        {
          id: 'fill_pdf_form', label: 'Fill PDF Form',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M8 13h8" /><path d="M8 17h4" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <Toggle label="Auto-fill with AI" value={s.fillPdfAutoFillWithAi ?? false} onChange={(v) => ch({ fillPdfAutoFillWithAi: v })} />
              <Toggle label="Flatten after" value={s.fillPdfFlattenAfter ?? false} onChange={(v) => ch({ fillPdfFlattenAfter: v })} />
            </>
          ),
        },
        {
          id: 'stamp_document', label: 'Stamp Document',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="14" width="18" height="7" rx="2" /><path d="M8 14V8a4 4 0 0 1 8 0v6" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <TextInput label="Stamp text" value={s.stampText ?? ''} placeholder="e.g. APPROVED" onChange={(v) => ch({ stampText: v })} />
              <ColorPicker label="Color" value={s.stampColor ?? '#E8534A'} onChange={(v) => ch({ stampColor: v })} />
              <Slider label="Opacity" value={s.stampOpacity ?? 80} min={10} max={100} onChange={(v) => ch({ stampOpacity: v })} formatValue={(v) => v + '%'} />
            </>
          ),
        },
        {
          id: 'decline_void', label: 'Decline & Void',
          icon: (s) => <svg {...svgProps()} stroke={s}><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>,
        },
      ],
    },
    // ──────── GENERATE ────────
    {
      id: 'generate',
      label: 'GENERATE',
      tools: [
        {
          id: 'qr_code', label: 'QR Code',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="3" height="3" /><path d="M21 14h-3v3" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <NumberInput label="Size (px)" value={s.qrSize ?? 256} onChange={(v) => ch({ qrSize: v })} />
              <ColorPicker label="Foreground" value={s.qrForeground ?? '#000000'} onChange={(v) => ch({ qrForeground: v })} />
              <ColorPicker label="Background" value={s.qrBackground ?? '#FFFFFF'} onChange={(v) => ch({ qrBackground: v })} />
              <Select label="Error correction" value={s.qrErrorCorrection ?? 'M'} onChange={(v) => ch({ qrErrorCorrection: v as ToolPanelSettings['qrErrorCorrection'] })} options={[{ value: 'L', label: 'Low (7%)' }, { value: 'M', label: 'Medium (15%)' }, { value: 'Q', label: 'Quartile (25%)' }, { value: 'H', label: 'High (30%)' }]} />
            </>
          ),
        },
        {
          id: 'barcode', label: 'Barcode',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="5" width="18" height="14" rx="1" /><path d="M6 8v8" /><path d="M10 8v8" /><path d="M13 8v8" /><path d="M18 8v8" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <TextInput label="Content" value={s.barcodeContent ?? ''} placeholder="Data to encode" onChange={(v) => ch({ barcodeContent: v })} />
              <NumberInput label="Width" value={s.barcodeWidth ?? 200} onChange={(v) => ch({ barcodeWidth: v })} />
              <NumberInput label="Height" value={s.barcodeHeight ?? 80} onChange={(v) => ch({ barcodeHeight: v })} />
              <Toggle label="Show text" value={s.barcodeShowText ?? true} onChange={(v) => ch({ barcodeShowText: v })} />
            </>
          ),
        },
        {
          id: 'invoice_pdf', label: 'Invoice PDF',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M8 13h3" /><path d="M8 17h3" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <SegmentedControl label="Template" value={s.invoiceTemplate ?? 'simple'} onChange={(v) => ch({ invoiceTemplate: v })} options={[{ value: 'simple', label: 'Simple' }, { value: 'detailed', label: 'Detailed' }]} />
              <TextInput label="Currency" value={s.invoiceCurrency ?? 'USD'} onChange={(v) => ch({ invoiceCurrency: v })} />
              <NumberInput label="Tax rate (%)" value={s.invoiceTaxRate} placeholder="0" onChange={(v) => ch({ invoiceTaxRate: v })} />
            </>
          ),
        },
        {
          id: 'certificate_pdf', label: 'Certificate PDF',
          icon: (s) => <svg {...svgProps()} stroke={s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><circle cx="12" cy="15" r="3" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <SegmentedControl label="Template" value={s.certificatePdfTemplate ?? 'minimal'} onChange={(v) => ch({ certificatePdfTemplate: v })} options={[{ value: 'minimal', label: 'Minimal' }, { value: 'formal', label: 'Formal' }]} />
          ),
        },
      ],
    },
    // ──────── PROTECT & SECURE ────────
    {
      id: 'protect',
      label: 'PROTECT & SECURE',
      tools: [
        {
          id: 'password_protect', label: 'Password Protect',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <TextInput label="Open password" value={s.openPassword ?? ''} onChange={(v) => ch({ openPassword: v })} />
              <TextInput label="Permissions password" value={s.permissionsPassword ?? ''} onChange={(v) => ch({ permissionsPassword: v })} />
              <Toggle label="Restrict printing" value={s.restrictPrinting ?? false} onChange={(v) => ch({ restrictPrinting: v })} />
              <Toggle label="Restrict editing" value={s.restrictEditing ?? false} onChange={(v) => ch({ restrictEditing: v })} />
              <Toggle label="Restrict copying" value={s.restrictCopying ?? false} onChange={(v) => ch({ restrictCopying: v })} />
            </>
          ),
        },
        {
          id: 'remove_password', label: 'Remove Password',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 9.9-1" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <TextInput label="Current password" value={s.currentPassword ?? ''} onChange={(v) => ch({ currentPassword: v })} />
          ),
        },
        {
          id: 'redact_content', label: 'Redact Content',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="18" height="18" rx="2" /><rect x="7" y="7" width="4" height="1" rx="0.5" /><rect x="7" y="11" width="6" height="1" rx="0.5" /><rect x="7" y="15" width="3" height="1" rx="0.5" /></svg>,
        },
        {
          id: 'add_watermark', label: 'Add Watermark',
          icon: (s) => <svg {...svgProps()} stroke={s}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 15l5-5 3 3 4-4 6 6" /></svg>,
          hasSettings: true,
          renderSettings: ({ settings: s, onSettingsChange: ch }) => (
            <>
              <TextInput label="Text" value={s.watermarkText ?? ''} placeholder="e.g. CONFIDENTIAL" onChange={(v) => ch({ watermarkText: v })} />
              <Slider label="Opacity" value={s.watermarkOpacity ?? 30} min={5} max={100} onChange={(v) => ch({ watermarkOpacity: v })} formatValue={(v) => v + '%'} />
              <ColorPicker label="Color" value={s.watermarkColor ?? '#888888'} onChange={(v) => ch({ watermarkColor: v })} />
              <Toggle label="Tile / repeat" value={s.watermarkTile ?? false} onChange={(v) => ch({ watermarkTile: v })} />
            </>
          ),
        },
      ],
    },
  ];
}

// ─── Search helpers ───────────────────────────────────

export function filterToolCategories(categories: ToolCategory[], query: string): ToolCategory[] {
  const q = query.trim().toLowerCase();
  if (!q) return categories;
  return categories
    .map((cat) => ({
      ...cat,
      tools: cat.tools.filter((t) => {
        const blob = `${t.label} ${t.id.replace(/_/g, ' ')} ${cat.label}`.toLowerCase();
        return blob.includes(q);
      }),
    }))
    .filter((cat) => cat.tools.length > 0);
}
